import { 
    BODY_BACKGROUND, 
    BORDER,
    DARK_MODE,
    GRAY_SCALE,
    THEME_COLOR
} from 'constants/ThemeConstant'

export function rgba(hex, opacity = 1) {
    if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      throw new Error('Invalid Hex');
    }
  
    const decimal = parseInt(hex.substring(1), 16);
  
    const r = (decimal >> 16) & 255;
    const g = (decimal >> 8) & 255;
    const b = decimal & 255;
  
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const baseTheme = {
    borderRadius: 10,
    colorBgBody: BODY_BACKGROUND,
    colorBorder: BORDER.BASE_COLOR,
    colorError: THEME_COLOR.VOLCANO,
    colorInfo: THEME_COLOR.BLUE,
    colorPrimary: THEME_COLOR.BLUE,
    colorSuccess: THEME_COLOR.CYAN,
    colorText: GRAY_SCALE.GRAY,
    colorWarning: THEME_COLOR.GOLD,
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 36,
    fontFamily: `'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji'`,
    fontSizeHeading2: 22,
    fontSizeHeading4: 17
}

const getBaseComponentThemeConfig = (
    {
        Button = {},
        Calendar = {}, 
        Card = {},
        DatePicker = {},
        Dropdown = {},
        Input = {},
        Layout = {},
        Menu = {},
        Select = {},
        Table = {}
    } = {}
) => {
    return {
        Breadcrumb: {
            colorBgTextHover: 'transparent',
            colorText: baseTheme.colorPrimary,
            colorTextDescription: GRAY_SCALE.GRAY_LIGHT
        },
        Button: {
            ...Button
        },
        Calendar: {
            ...Calendar
        },
        Card: {
            colorTextHeading: GRAY_SCALE.GRAY_DARK,
            paddingLG: 20,
            ...Card
        },
        Checkbox: {
            borderRadiusSM: 4,
            lineWidth: 2
        },
        DatePicker: {
            borderRadiusSM: 6,
            controlHeight: 40,
            controlHeightSM: 26,
            lineWidthBold: 0,
            ...DatePicker
        },
        Dropdown: {
            borderRadiusLG: 10,
            controlHeight: 37,
            controlPaddingHorizontal: 20,
            paddingXXS: '4px 0px',
            ...Dropdown
        },
        Input: {
            ...Input
        },
        Layout: {
            colorBgBody: BODY_BACKGROUND,
            colorBgHeader: GRAY_SCALE.WHITE,
            ...Layout
        },
        Menu: {
            colorActiveBarHeight: 0,
            colorActiveBarWidth: 3,
            colorItemBg: 'transparent',
            colorItemBgHover: 'transparent',
            colorItemBgSelected: rgba(baseTheme.colorPrimary, 0.1),
            colorItemTextHover: baseTheme.colorPrimary,
            colorItemTextSelected: baseTheme.colorPrimary,
            colorItemTextSelectedHorizontal: baseTheme.colorPrimary,
            colorSubItemBg: 'transparent',
            controlHeightLG: 40,
            controlHeightSM: 22,
            itemMarginInline: 0,
            radiusItem: 0,
            ...Menu
        },
        Pagination: {
            borderRadius: '50%',
            colorBgContainer: baseTheme.colorPrimary,
            colorPrimary:  GRAY_SCALE.WHITE,
            colorPrimaryHover: GRAY_SCALE.WHITE,
            paginationItemSize: 30,
        },
        Radio: {
            fontSizeLG: 18
        },
        Select: {
            controlHeight: 40,
            controlHeightSM: 30,
            controlItemBgActive: rgba(baseTheme.colorPrimary, 0.1),
            paddingXXS: '4px 0px',
            ...Select
        },
        Slider: {
            colorPrimaryBorder: rgba(baseTheme.colorPrimary, 0.8),
            colorPrimaryBorderHover: baseTheme.colorPrimary
        },
        Steps: {
            controlHeight: 32,
            waitIconColor: GRAY_SCALE.GRAY_LIGHT,
            wireframe: true
        },
        Table: {
            colorText: GRAY_SCALE.GRAY,
            tableHeaderBg: 'transparent',
            ...Table
        },
        Tag: {
            lineHeight: 2.1
        },
        Timeline: {
            lineType: 'dashed'
        },
        Tooltip: {
            colorBgDefault: rgba(GRAY_SCALE.DARK, 0.75),
            controlHeight: 32
        },
        TreeSelect: {
            controlHeightSM: 24
        }
    }
}

export const lightTheme = {
    components: getBaseComponentThemeConfig(),
    token: {
        ...baseTheme,
        colorTextBase: GRAY_SCALE.GRAY,
    }
}



export const darkTheme = {
    components: getBaseComponentThemeConfig({
        Button: {
            colorBorder: DARK_MODE.BORDER_BASE_COLOR,
            colorText: DARK_MODE.TEXT_COLOR,
            controlOutline: 'transparent',
        },
        Calendar: {
            controlItemBgActive: '#303a4e'
        },
        Card: {
            colorTextHeading: DARK_MODE.HEADING_COLOR,
        },
        DatePicker: {
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
            controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
            controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
            controlOutline: 'transparent',
        },
        Dropdown: {
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW
        },
        Input: {
            controlOutline: 'transparent',
        },
        Layout: {
            colorBgBody: '#1b2531',
            colorBgHeader: DARK_MODE.BG_COLOR
        },
        Menu: {
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
            colorActiveBarWidth: 0,
            colorItemBgSelected: 'transparent',
            colorItemText: DARK_MODE.TEXT_COLOR,
            colorItemTextHover: GRAY_SCALE.WHITE,
            colorItemTextSelected: GRAY_SCALE.WHITE,
            colorItemTextSelectedHorizontal: GRAY_SCALE.WHITE
        },
        Select: {
            boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
            controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
            controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
            controlOutline: 'transparent'
        },
        Table: {
            colorText: DARK_MODE.TEXT_COLOR,
            colorTextHeading: DARK_MODE.HEADING_COLOR
        },
    }),
    token: {
        ...baseTheme,
        colorBgBase: DARK_MODE.BG_COLOR,
        colorBorder: DARK_MODE.BORDER_BASE_COLOR,
        colorText: DARK_MODE.TEXT_COLOR,
        colorTextBase: DARK_MODE.TEXT_COLOR,
    }
}